import React, { useEffect, useRef, useState } from 'react';
import { Input, Spinner } from '@nextui-org/react';
import { MapPin, Search } from 'lucide-react';
import userService, { GeocodeResult } from '../services/userService';

interface Props {
  value: string;
  onValueChange: (value: string) => void;
  onSelect: (result: GeocodeResult) => void;
  label?: string;
  placeholder?: string;
  description?: string;
  isDisabled?: boolean;
  classNames?: any;
}

const AddressSearchInput: React.FC<Props> = ({
  value,
  onValueChange,
  onSelect,
  label = 'Adresse suchen',
  placeholder = 'z.B. Musterstraße 1, 12345 Berlin',
  description,
  isDisabled = false,
  classNames,
}) => {
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastSelectedRef = useRef<string>('');

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = value.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    if (trimmed === lastSelectedRef.current) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      const found = await userService.geocodeAddress(trimmed);
      setResults(found);
      setIsSearching(false);
      setIsOpen(found.length > 0);
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  const handleSelect = (result: GeocodeResult) => {
    const label = result.formattedAddress || '';
    lastSelectedRef.current = label;
    onValueChange(label);
    onSelect(result);
    setIsOpen(false);
    setResults([]);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Input
        label={label}
        placeholder={placeholder}
        description={description}
        value={value}
        onValueChange={(v) => {
          onValueChange(v);
          if (v.trim().length >= 3) setIsOpen(true);
        }}
        onFocus={() => {
          if (results.length > 0) setIsOpen(true);
        }}
        startContent={<MapPin size={16} className="text-gray-400" />}
        endContent={
          isSearching ? (
            <Spinner size="sm" color="primary" />
          ) : (
            <Search size={16} className="text-gray-500" />
          )
        }
        variant="bordered"
        isDisabled={isDisabled}
        classNames={classNames}
        autoComplete="off"
      />
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl">
          {results.map((r, idx) => (
            <button
              key={`${r.latitude}-${r.longitude}-${idx}`}
              type="button"
              onClick={() => handleSelect(r)}
              className="w-full text-left px-3 py-2 hover:bg-zinc-800 border-b border-zinc-800 last:border-b-0 transition-colors"
            >
              <div className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 text-[#3fced1] shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">
                    {r.formattedAddress || '—'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {r.latitude?.toFixed(4)}, {r.longitude?.toFixed(4)}
                    {r.country ? ` · ${r.country}` : ''}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressSearchInput;
